import React, {FC, useState} from 'react';
import styled, {css} from 'styled-components';

import {Skeleton, SkeletonColorSchemesEnum} from '../../../../../../../../../src/components/common/skeleton';
import {CrmSyncSourcesEnum} from '../../../../../../../../../src/models/api/crmSyncModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../../../../../../src/services/tracking';
import {Trans} from '../../../../../../../../shared/helpers/src/internationalization/react';
import {useInitialEffect} from '../../../../../../../../shared/helpers/src/react/hooks/useEffectHelpers';
import {AsyncHandler, HandlerOf} from '../../../../../../../../shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../../../shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../../../shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../../../../../../shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../../../../shared/ui/src/styles/fontStyles';
import {PaletteColorsEnum} from '../../../../../../../../shared/ui/src/styles/paletteModel';
import {UserThemeConsumer} from '../../../../../../../../shared/ui/src/theme/userThemeConsumer';
import {useCrmPageConfig} from '../crmSyncsPageConfig/crmSyncsPageConfig';
import {CrmSyncsSectionWrapper} from '../crmSyncsSectionWrapper';
import {CrmSyncLoginContainerProps} from './crmSyncsLoginContainer';
import {
  formatExistingMicrosoftDynamicsUrl,
  formatMicrosoftDynamicsUrl,
  isMicrosoftDynamicsEnvironmentUrlValid,
  MicrosoftDynamicsErrorsEnum,
  MicrosoftDynamicsUrl,
} from './microsoftDynamicsURL';

/*
 * Style.
 */

const StyledButton = styled(Button)`
  display: inline-block;
  margin-bottom: 5px;
`;

const StyledButtonContentDiv = styled.div`
  display: flex;
  flex-flow: row;
`;

const StyledButtonTextWrapperDiv = styled.div`
  flex: 1;
`;

interface StyledButtonImageWrapperDivProps {
  $isDisabled?: boolean;
}

const StyledButtonImageWrapperDiv = styled.div<StyledButtonImageWrapperDivProps>`
  background: ${(p) => p.theme.foreground};
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex: 0 0 20px;
  height: 20px;
  margin: -1px 8px -2px -9px;

  ${(p) =>
    p.$isDisabled &&
    css`
      filter: grayscale(1);
    `};
`;

const StyledDetailsWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  flex-wrap: wrap;
  gap: 8px;
`;

interface StyledUsernameWrapperDivProps {
  $hasExpiredCredentials: boolean;
}

const StyledUsernameWrapperDiv = styled.div<StyledUsernameWrapperDivProps>`
  display: inline-flex;
  flex-flow: row;
  background: ${(p) => p.theme.palette.green.shade20};
  border-radius: 15px;
  padding: 7px 12px 7px 7px;

  ${(p) =>
    p.$hasExpiredCredentials &&
    css`
      background: ${p.theme.palette.red.shade20};
    `};
`;

const StyledIcon = styled(Icon)`
  flex: 0 0 16px;
  margin-right: 8px;
`;

const StyledUsernameDetailsDiv = styled.div`
  flex: 1;
  line-height: 16px;
`;

const StyledUsernameSpan = styled.span`
  font-weight: ${fontWeights.semibold};
`;

const StyledCredentialsErrorDiv = styled.div`
  color: ${(p) => p.theme.palette.red.shade40};
  font-weight: ${fontWeights.medium};
  font-size: ${fontSizes.verySmall};
  flex: 0 0 100%;
`;

/*
 * Component.
 */

export const CrmSyncsLogin: FC<CrmSyncLoginContainerProps> = (props) => {
  const {
    isCrmSyncLoading,
    isDisabled,
    isAdmin,
    source,
    username,
    hasExpiredCredentials,
    isConnected,
    isIntegrationTypesLoading,
    integration,
    integrationType,
    existingMicrosoftDynamicsEnvironmentUrl,
    createIntegrationAsync,
    updateIntegrationAsync,
    fetchIntegrationTypesAsync,
    fetchAllIntegrationsAsync,
    signOutOfIntegrationAsync,
    signInToIntegrationAccount,
  } = props;
  const [microsoftDynamicsEnvironmentUrlError, setMicrosoftDynamicsEnvironmentUrlError] = useState<
    MicrosoftDynamicsErrorsEnum | undefined
  >();
  const [microsoftDynamicsEnvironmentUrl, setMicrosoftDynamicsEnvironmentUrl] = useState<string>(
    formatExistingMicrosoftDynamicsUrl(existingMicrosoftDynamicsEnvironmentUrl),
  );

  useInitialEffect(() => {
    // update with latest integration data
    fetchIntegrationTypesAsync();
    fetchAllIntegrationsAsync();
  });

  const onSignIn = async (shouldUseTestEnv: boolean) => {
    if (source === CrmSyncSourcesEnum.MICROSOFT_DYNAMICS) {
      const error = isMicrosoftDynamicsEnvironmentUrlValid(microsoftDynamicsEnvironmentUrl);
      if (error) {
        setMicrosoftDynamicsEnvironmentUrlError(error);
        if (error === MicrosoftDynamicsErrorsEnum.INVALID_URL && microsoftDynamicsEnvironmentUrl) {
          trackInterfaceEvent(InterfaceEventsEnum.CONTACTS_MANAGER_CRM_SYNC_DYNAMICS_INVALID_URL, {
            // per https://github.com/frontapp/front-client/blob/master/docs/tracking.md#other-forms-of-tracking can only send up to 200 characters
            url: microsoftDynamicsEnvironmentUrl.substring(0, 199),
          });
        }
        return;
      }
    }

    const stateData =
      source === CrmSyncSourcesEnum.MICROSOFT_DYNAMICS
        ? formatMicrosoftDynamicsUrl(microsoftDynamicsEnvironmentUrl)
        : undefined;

    // check if we need to auto create and/or enable the integration
    try {
      if (!integration && integrationType) {
        await createIntegrationAsync(integrationType, {isEnabled: true, settings: {}});
      } else if (integration && !integration.isEnabled) {
        await updateIntegrationAsync(integration.id, {isEnabled: true});
      }
    } catch {
      // TODO: Should handle error when creating/updating
    }

    signInToIntegrationAccount(shouldUseTestEnv, stateData);
  };

  const shouldRenderConnectButton = !isConnected || (!username && hasExpiredCredentials);
  const {logoSm, displayName, loginDescription} = useCrmPageConfig(source);
  const crmSourceName = source === CrmSyncSourcesEnum.OUTLOOK_DIRECTORY ? 'Outlook' : displayName;
  return (
    <CrmSyncsSectionWrapper
      title={<Trans id="crm-sync-page-login-title">Connect {displayName} to Front</Trans>}
      description={
        loginDescription || (
          <Trans id="crm-sync-page-login-description">{displayName} administrator privileges required.</Trans>
        )
      }
      // We only need to wait for the integration types if there is not an integration because they are used to create the integration
      // See onSignIn
      isLoading={(!integration && isIntegrationTypesLoading) || isCrmSyncLoading}
      renderLoadingState={renderLoadingState}
      isDisabled={isDisabled}
    >
      {source === CrmSyncSourcesEnum.MICROSOFT_DYNAMICS && (
        <MicrosoftDynamicsUrl
          // can only update url if you are connecting or reconnecting since it's used in connect oauth request
          isDisabled={!shouldRenderConnectButton}
          url={microsoftDynamicsEnvironmentUrl}
          error={microsoftDynamicsEnvironmentUrlError}
          onChange={(url: string) => {
            if (microsoftDynamicsEnvironmentUrlError) {
              setMicrosoftDynamicsEnvironmentUrlError(undefined);
            }
            setMicrosoftDynamicsEnvironmentUrl(url);
          }}
        />
      )}
      {shouldRenderConnectButton
        ? renderConnectButton(props, crmSourceName, logoSm, isDisabled || !isAdmin, onSignIn)
        : renderUsername(props, crmSourceName, signOutOfIntegrationAsync)}
    </CrmSyncsSectionWrapper>
  );
};

/*
 * Helpers.
 */

function renderLoadingState() {
  return <Skeleton width="600px" height="60px" borderRadius="8px" scheme={SkeletonColorSchemesEnum.GREY10} />;
}

function renderLogInTo(crmSyncSourceName: string) {
  return <Trans id="crm-sync-page-log-in">Log in to {crmSyncSourceName}</Trans>;
}

function maybeRenderUsernameDisplayText(
  hasExpiredCredentials: boolean,
  crmSyncSourceName: string,
  username?: string,
) {
  if (hasExpiredCredentials) {
    return <StyledUsernameSpan>{username}</StyledUsernameSpan>;
  }
  if (!username) {
    return <Trans id="crm-sync-page-account-authorized">{crmSyncSourceName} account authorized</Trans>;
  }
  return (
    <>
      <StyledUsernameSpan>{username}</StyledUsernameSpan>{' '}
      <Trans id="crm-sync-page-user-authorized">authorized</Trans>
    </>
  );
}

function renderUsername(
  props: CrmSyncLoginContainerProps,
  crmSyncSourceName: string,
  onSignOut: AsyncHandler,
) {
  const {hasExpiredCredentials, isAdmin, isDisabled, username} = props;
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledDetailsWrapperDiv>
          <StyledUsernameWrapperDiv $hasExpiredCredentials={hasExpiredCredentials}>
            <StyledIcon
              name={hasExpiredCredentials ? 'warningFilledSmall' : 'checkmarkCircleSmall'}
              color={hasExpiredCredentials ? theme.palette.red.shade40 : theme.palette.green.shade40}
            />
            <StyledUsernameDetailsDiv>
              {maybeRenderUsernameDisplayText(hasExpiredCredentials, crmSyncSourceName, username)}
            </StyledUsernameDetailsDiv>
          </StyledUsernameWrapperDiv>
          {isAdmin && (
            <Button
              variant={ButtonVariantsEnum.LINK}
              color={PaletteColorsEnum.RED}
              isDisabled={isDisabled}
              onClick={onSignOut}
            >
              <Trans id="crm-sync-page-disconnect">Disconnect</Trans>
            </Button>
          )}
          {hasExpiredCredentials && (
            <StyledCredentialsErrorDiv>
              <Trans id="crm-sync-page-error-expired-credentials">
                Unable to connect to {crmSyncSourceName}. Please re-authorize.
              </Trans>
            </StyledCredentialsErrorDiv>
          )}
        </StyledDetailsWrapperDiv>
      )}
    </UserThemeConsumer>
  );
}

function maybeRenderCredentialsError(crmSyncSourceName: string, hasExpiredCredentials: boolean) {
  if (!hasExpiredCredentials) {
    return null;
  }
  return (
    <StyledCredentialsErrorDiv>
      <Trans id="crm-sync-page-error-connecting-to-crm">
        An error occurred while connecting to {crmSyncSourceName}. Please re-connect credentials.
      </Trans>
    </StyledCredentialsErrorDiv>
  );
}

function renderConnectButton(
  props: CrmSyncLoginContainerProps,
  crmSyncSourceName: string,
  logoSm: string,
  isDisabled: boolean,
  onSignIn: HandlerOf<boolean>,
) {
  const {hasExpiredCredentials} = props;

  return (
    <>
      <StyledButton
        variant={ButtonVariantsEnum.PRIMARY}
        isDisabled={isDisabled}
        onClick={(event) => onSignIn(event.shiftKey)}
      >
        <StyledButtonContentDiv>
          <StyledButtonImageWrapperDiv $isDisabled={isDisabled}>
            <img src={logoSm} height={20} width={20} alt="crm logo" />
          </StyledButtonImageWrapperDiv>
          <StyledButtonTextWrapperDiv>{renderLogInTo(crmSyncSourceName)}</StyledButtonTextWrapperDiv>
        </StyledButtonContentDiv>
      </StyledButton>
      {maybeRenderCredentialsError(crmSyncSourceName, hasExpiredCredentials)}
    </>
  );
}
